import { IdCodeDef } from "./type/idcode-def";
import { ConfigDef } from "./type/config-def";
import { QueryDef } from "./type/query-def";
import { NodeDef } from "./type/node-def";
import {
  CausalAssetAnalysisListResponse,
  CausalAssetAnalysisListRequest,
  DwModelListResponse,
  DwModelListRequest,
  DwModelRequest,
  DatasetRequest,
  DwModel
} from "./type/causal-asset";

import { ConfigAdapter } from "./adapter/config-def";
import { IdCodeAdapter } from "./adapter/idcode-def";
import { QueryAdapter } from "./adapter/query-def";
import { NodeAdapter } from "./adapter/node-def";
import { adaptCausalAssetAnalysisListResponse, adaptDwModelListResponse } from "./adapter/causal-asset";

import { ISharedRepo } from "./irepo";

import { TError } from "../../types/error";

/**
 * data_properties の応答をマージする。
 * base（全列）を土台に、scoped（アセット指定）の同じ value を持つ項目で上書きする。
 * scoped にしか無い列も取りこぼさないよう末尾に追加する。
 */
function mergeDataProperties(base: any, scoped: any): any {
  const baseList: any[] = base?.data_properties_list ?? [];
  const scopedList: any[] = scoped?.data_properties_list ?? [];
  if (scopedList.length === 0) return base;

  const scopedByValue = new Map<any, any>(
    scopedList.map((e: any) => [e.value, e])
  );

  const merged = baseList.map((e: any) => {
    const override = scopedByValue.get(e.value);
    if (!override) return e;
    scopedByValue.delete(e.value);
    return { ...e, ...override };
  });

  // base に無い列（アセット側だけが持つ列）を落とさない
  scopedByValue.forEach((e) => merged.push(e));

  return { ...base, data_properties_list: merged };
}

export class SharedUsecase {
  private repo: ISharedRepo;
  private error: (error: TError) => void;

  constructor(repo: ISharedRepo, error: (error: TError) => void) {
    this.repo = repo;
    this.error = error;
  }

  /**
   * ノード名・単位・階層(layer_no) のマスタ (data_properties) を取得する。
   *
   * req は asset_id（任意）。指定するとそのアセットの設定を取得するが、
   * **応答はそのアセットに含まれる列だけ**になる。アウトカムノード一覧は
   * アセットに依存しない全列を返すため、asset_id 指定だけで組み立てると
   * アセット外の列に名前が付かず、画面に `column_xxx_508` のような生の値が出る。
   *
   * そこで既定（全列）を土台にし、アセット側の設定を同じ value で上書きする。
   * アセット固有の階層・単位を効かせつつ、名前はすべての列で引けるようにする。
   */
  async fetchConfigDef(req?: string): Promise<ConfigDef> {
    try {
      const base = await this.repo.fetchConfigDef(ConfigAdapter.toInputDTO(undefined));
      if (!req) return ConfigAdapter.toOutputDTO(base);

      const scoped = await this.repo.fetchConfigDef(ConfigAdapter.toInputDTO(req));
      return ConfigAdapter.toOutputDTO(mergeDataProperties(base, scoped));
    } catch (error) {
      this.error({ id: "shared", error });
      throw error;
    }
  }

  async fetchQueryDef(): Promise<QueryDef> {
    try {
      const data = await this.repo.fetchQueryDef();
      return QueryAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: "shared", error });
      throw error;
    }
  }

  async fetchIdCodeDef(req: QueryDef): Promise<IdCodeDef> {
    try {
      const dto = IdCodeAdapter.toInputDTO(req);
      const data = await this.repo.fetchIdCodeDef(dto);
      return IdCodeAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: "shared", error });
      throw error;
    }
  }

  async fetchNodeDef(isSmallytics?: boolean): Promise<NodeDef> {
    try {
      const dto = NodeAdapter.toInputDTO(isSmallytics);
      const data = await this.repo.fetchNodeDef(dto);
      return NodeAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: "shared", error });
      throw error;
    }
  }

  async fetchCausalAssetAnalysisList(req: CausalAssetAnalysisListRequest): Promise<CausalAssetAnalysisListResponse> {
    try {
      const data = await this.repo.fetchCausalAssetAnalysisList(req);
      return adaptCausalAssetAnalysisListResponse(data);
    } catch (error) {
      this.error({ id: "shared", error });
      throw error;
    }
  }

  async fetchDwModelList(req: DwModelListRequest): Promise<DwModelListResponse> {
    try {
      const data = await this.repo.fetchDwModelList(req);
      return adaptDwModelListResponse(data);
    } catch (error) {
      this.error({ id: "shared", error });
      throw error;
    }
  }

  async fetchModel(req: DwModelRequest): Promise<any> {
    try {
      const bnmodel = await this.repo.fetchDwModel(req);
      const shape = await this.repo.fetchGraphParameters(req);
      const res = await this.repo.fetchDataset({ dataset_metadata_id: bnmodel.dataset_metadata_id });

      const columnsObject : any = {};
      const columnsLabelObject : any = {};
      if (res?.column_properties) {
        for (let item of res.column_properties) {
          columnsObject[item.value] = item;
          columnsLabelObject[item.name] = item;
        }
      }
      const dataset = {
        columns: columnsObject,
        columnsByLabel: columnsLabelObject,
      };

      return { ...bnmodel, id: req.model_id, graph_parameter: shape, dataset };
    } catch (error) {
      this.error({ id: "shared", error });
      throw error;
    }
  }

  async fetchAllDwModelList(): Promise<DwModel[]> {
    // まずanalysisリストを取得
    const analysisResponse = await this.repo.fetchCausalAssetAnalysisList({
      page: 1,
      items_per_page: 1000,
      is_core_model: true
    });
    
    // 各analysisのidを使ってdwmodelを取得
    const allDwModels: DwModel[] = [];
    for (const analysis of analysisResponse.analyses || []) {
      try {
        const dwModelResponse = await this.repo.fetchDwModelList({
          analysis_id: analysis.id,
          page: 1,
          items_per_page: 1000,
          is_fixed: true,
          is_core_model: true
        });
        allDwModels.push(...(dwModelResponse.dwmodels || []));
      } catch (error) {
        console.error(`Failed to fetch dwmodels for analysis ${analysis.id}:`, error);
      }
    }
    
    return allDwModels;
  }
}
